// src/firebase/settings-functions.js
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { functions, db } from "./firebase";

// SHARED CACHE UTILITY
// This ensures that if we fetched the store doc for Settings, we don't re-fetch for Home Page
const getCachedStoreDoc = async (storeId) => {
    const cacheKey = `storeDoc_${storeId}`;
    const cached = sessionStorage.getItem(cacheKey);
    const cachedTs = sessionStorage.getItem(`${cacheKey}_ts`);

    // 10 Minute Cache for Settings (Settings rarely change)
    if (cached && cachedTs && (Date.now() - parseInt(cachedTs) < 10 * 60 * 1000)) {
        return JSON.parse(cached);
    }

    // Fetch Fresh
    const storeDoc = await getDoc(doc(db, "stores", storeId));
    if (!storeDoc.exists()) throw new Error("Store not found");

    const data = storeDoc.data();

    // Save to Cache
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    sessionStorage.setItem(`${cacheKey}_ts`, Date.now().toString());

    return data;
};

// Clear cache for a specific store
const clearStoreDocCache = (storeId) => {
    const cacheKey = `storeDoc_${storeId}`;
    sessionStorage.removeItem(cacheKey);
    sessionStorage.removeItem(`${cacheKey}_ts`);
};

/**
 * OPTIMIZED: Uses shared cache to prevent redundant reads
 */
export const getStoreSettings = async (storeId, options = {}) => {
    const { language = 'en', fullMultilingual = false, simplified = false } = options;

    try {
        // USE CACHED FETCH
        const data = await getCachedStoreDoc(storeId);

        // Base settings that are always included
        const baseSettings = {
            email: data.email || "",
            phone: data.phone || "",
            currency: data.currency || (language === 'ar' ? "AED" : null),
            logoUrl: data.logoUrl || "",
            socialMedia: data.socialMedia || {},
            taxRate: data.taxRate !== undefined ? data.taxRate : 0,
            taxIncluded: data.taxIncluded || false,
            minimumSpendForFreeDelivery: data.minimumSpendForFreeDelivery || null,
            promoCodesEnabled: data.promoCodesEnabled || false
        };

        // Handle multilingual content based on options
        if (fullMultilingual) {
            // Return full multilingual structure for admin
            return {
                ...baseSettings,
                multilingualContent: data.multilingualContent || {
                    en: {
                        name: data.name || "",
                        description: data.description || ""
                    },
                    ar: {
                        name: "",
                        description: ""
                    }
                },
                address: data.address || "",
                domain: data.domain || "", // Include custom domain
                requiresShipping: data.requiresShipping !== false, // Include shipping requirement
                brandColors: data.brandColors || {
                    primary: "#182957",
                    hover: "#233a79",
                    text: "#181317",
                    light: "#e7edf7"
                },
                shippingOptions: data.shippingOptions || [],
                paymentMethods: data.paymentMethods || {
                    creditCard: true,
                    paypal: false,
                    applePay: false,
                    cashOnDelivery: true
                },
                bankTransferDetails: data.bankTransferDetails || { en: "", ar: "" },
                availableCountries: data.availableCountries || [],
                homePageContent: data.homePageContent || {
                    heroSection: {
                        title: "Handmade by Emirati Hands",
                        subtitle: "Crafted with love, just for you.",
                        mediaType: "video",
                        mediaUrl: "/vid.mp4",
                        overlayOpacity: 0.5
                    },
                    storySection: {
                        title: "THERE IS ELEGANCE IN SIMPLICITY",
                        description: "Inspired by heritage, crafted with love. Our designs blend tradition with modern luxury, ensuring every abaya tells a story of confidence and grace.",
                        buttonText: "DISCOVER MORE",
                        buttonLink: "/products"
                    }
                },
                promoSettings: data.promoSettings || {
                    enabled: false,
                    title: "",
                    description: "",
                    backgroundColor: "#ff6b6b",
                    textColor: "#ffffff",
                    buttonText: "Shop Now",
                    buttonLink: "",
                    showButton: true,
                    displayDuration: 0,
                    position: "top"
                },
                announcementSettings: data.announcementSettings || {
                    enabled: false,
                    message: "",
                    type: "info",
                    backgroundColor: "#4a90e2",
                    textColor: "#ffffff",
                    showCloseButton: true,
                    autoHide: false,
                    autoHideDuration: 5000,
                    blinking: false
                }
            };
        }

        if (simplified) {
            // Return simplified format for frontend use with specific language
            return {
                ...baseSettings,
                name: data.multilingualContent?.[language]?.name || data.name || "",
                description: data.multilingualContent?.[language]?.description || data.description || "",
                address: data.address || "",
                requiresShipping: data.requiresShipping !== false, // Include shipping requirement
                domain: data.domain || "", // Include custom domain
                brandColors: data.brandColors || {
                    primary: "#182957",
                    hover: "#233a79",
                    text: "#181317",
                    light: "#e7edf7"
                },
                shippingOptions: data.shippingOptions?.map(option => ({
                    id: option.id,
                    price: option.price,
                    name: option.multilingual?.[language]?.name || option.name || "",
                    deliveryTime: option.multilingual?.[language]?.deliveryTime || option.deliveryTime || ""
                })) || [],
                paymentMethods: data.paymentMethods || {
                    creditCard: true,
                    cashOnDelivery: true
                },
                bankTransferDetails: data.bankTransferDetails || { en: "", ar: "" },
                availableCountries: data.availableCountries?.map(country => ({
                    code: country.code,
                    name: country.multilingual?.[language]?.name || country.name || country.code
                })) || [
                        { code: "UAE", name: language === 'ar' ? "دولة الإمارات العربية المتحدة" : "United Arab Emirates" },
                        { code: "KSA", name: language === 'ar' ? "المملكة العربية السعودية" : "Saudi Arabia" },
                        { code: "QAT", name: language === 'ar' ? "قطر" : "Qatar" },
                        { code: "KWT", name: language === 'ar' ? "الكويت" : "Kuwait" },
                        { code: "BHR", name: language === 'ar' ? "البحرين" : "Bahrain" },
                        { code: "OMN", name: language === 'ar' ? "عُمان" : "Oman" }
                    ]
            };
        }

        // Default: Complete settings with multilingual processing (legacy behavior)
        return {
            ...baseSettings,
            name: data.multilingualContent?.en?.name || data.name || "",
            description: data.multilingualContent?.en?.description || data.description || "",

            // Process shipping options - handle both old and new format
            shippingOptions: (data.shippingOptions || []).map(option => {
                if (option.multilingual) {
                    // New multilingual format
                    return option;
                } else {
                    // Legacy format - convert to multilingual
                    return {
                        ...option,
                        multilingual: {
                            en: {
                                name: option.name || "",
                                deliveryTime: option.deliveryTime || ""
                            },
                            ar: {
                                name: "",
                                deliveryTime: ""
                            }
                        }
                    };
                }
            }),

            paymentMethods: data.paymentMethods || {
                creditCard: true,
                paypal: false,
                applePay: false,
                cashOnDelivery: true
            },

            bankTransferDetails: data.bankTransferDetails || { en: "", ar: "" },

            // Process countries - handle both old and new format
            availableCountries: (data.availableCountries || [
                { code: "UAE", name: "United Arab Emirates" },
                { code: "KSA", name: "Saudi Arabia" },
                { code: "QAT", name: "Qatar" },
                { code: "KWT", name: "Kuwait" },
                { code: "BHR", name: "Bahrain" },
                { code: "OMN", name: "Oman" }
            ]).map(country => {
                if (country.multilingual) {
                    // New multilingual format
                    return country;
                } else {
                    // Legacy format - convert to multilingual
                    return {
                        ...country,
                        multilingual: {
                            en: {
                                name: country.name || ""
                            },
                            ar: {
                                name: ""
                            }
                        }
                    };
                }
            }),

            // Store the multilingual content structure
            multilingualContent: data.multilingualContent || {
                en: { name: data.name || "", description: data.description || "" },
                ar: { name: "", description: "" }
            },
            homePageContent: data.homePageContent || {
                heroSection: {
                    title: "Handmade by Emirati Hands",
                    subtitle: "Crafted with love, just for you.",
                    mediaType: "video",
                    mediaUrl: "/vid.mp4",
                    overlayOpacity: 0.5
                },
                storySection: {
                    title: "THERE IS ELEGANCE IN SIMPLICITY",
                    description: "Inspired by heritage, crafted with love. Our designs blend tradition with modern luxury, ensuring every abaya tells a story of confidence and grace.",
                    buttonText: "DISCOVER MORE",
                    buttonLink: "/products"
                }
            },
            promoSettings: data.promoSettings || {
                enabled: false,
                title: "",
                description: "",
                backgroundColor: "#ff6b6b",
                textColor: "#ffffff",
                buttonText: "Shop Now",
                buttonLink: "",
                showButton: true,
                displayDuration: 0,
                position: "top"
            },
            announcementSettings: data.announcementSettings || {
                enabled: false,
                message: "",
                type: "info",
                backgroundColor: "#4a90e2",
                textColor: "#ffffff",
                showCloseButton: true,
                autoHide: false,
                autoHideDuration: 5000,
                blinking: false
            }
        };
    } catch (error) {
        console.error("Error getting store settings:", error);
        throw error;
    }
};

/**
 * Update store settings (calls secure Cloud Function)
 * @param {string} storeId - The ID of the store
 * @param {object} storeData - Store data with multilingualContent structure
 * @returns {Promise<object>} Result from Cloud Function
 */
export const updateStoreSettings = async (storeId, storeData) => {
    try {
        const updateStoreSettingsFunction = httpsCallable(functions, 'updateStoreSettings');
        const result = await updateStoreSettingsFunction({ storeId, storeData });

        // Clear cache after successful update to ensure fresh data on next fetch
        clearStoreDocCache(storeId);

        return result.data;
    } catch (error) {
        console.error("Error updating store settings:", error);
        throw error;
    }
};

/**
 * Update tax settings (calls secure Cloud Function)
 * @param {string} storeId - The ID of the store
 * @param {object} taxSettings - Tax settings (rate, included in price, etc.)
 * @returns {Promise<object>} Result from Cloud Function
 */
export const updateTaxSettings = async (storeId, taxSettings) => {
    try {
        const updateTaxSettingsFunction = httpsCallable(functions, 'updateTaxSettings');
        const result = await updateTaxSettingsFunction({ storeId, taxSettings });
        return result.data;
    } catch (error) {
        console.error("Error updating tax settings:", error);
        throw error;
    }
};

/**
 * Update multilingual shipping options (calls secure Cloud Function)
 * @param {string} storeId - The ID of the store
 * @param {array} shippingOptions - Array of shipping options with multilingual content
 * @returns {Promise<object>} Result from Cloud Function
 */
export const updateMultilingualShippingOptions = async (storeId, shippingOptions) => {
    try {
        const updateShippingFunction = httpsCallable(functions, 'updateMultilingualShippingOptions');
        const result = await updateShippingFunction({ storeId, shippingOptions });
        return result.data;
    } catch (error) {
        console.error("Error updating shipping options:", error);
        throw error;
    }
};

/**
 * Update multilingual available countries (calls secure Cloud Function)
 * @param {string} storeId - The ID of the store
 * @param {array} countries - Array of countries with multilingual names
 * @returns {Promise<object>} Result from Cloud Function
 */
export const updateMultilingualCountries = async (storeId, countries) => {
    try {
        const updateCountriesFunction = httpsCallable(functions, 'updateMultilingualCountries');
        const result = await updateCountriesFunction({ storeId, countries });
        return result.data;
    } catch (error) {
        console.error("Error updating countries:", error);
        throw error;
    }
};

/////////////////////////////
//HOME PAGE CONTENT FUNCTIONS
/////////////////////////////

/**
 * Update home page content settings (calls secure Cloud Function)
 * @param {string} storeId - The ID of the store
 * @param {object} homePageContent - Home page content configuration
 * @returns {Promise<object>} Result from Cloud Function
 */
export const updateHomePageContent = async (storeId, homePageContent) => {
    try {
        const updateHomePageContentFunction = httpsCallable(functions, 'updateHomePageContent');
        const result = await updateHomePageContentFunction({ storeId, homePageContent });
        return result.data;
    } catch (error) {
        console.error("Error updating home page content:", error);
        throw error;
    }
};

/**
 * OPTIMIZED: Uses shared cache. If getStoreSettings() ran first, this is FREE.
 */
export const getHomePageContent = async (storeId, language = 'en') => {
    try {
        // USE CACHED FETCH
        const data = await getCachedStoreDoc(storeId);
        const homePageContent = data.homePageContent || {
            heroSection: {
                en: {
                    title: "",
                    subtitle: "",
                },
                ar: {
                    title: "",
                    subtitle: "",
                },
                mediaType: "video", // "video" or "image"
                mediaUrl: "/vid.mp4",
                overlayOpacity: 0.5
            },
            storySection: {
                en: {
                    title: "",
                    description: "",
                    buttonText: "DISCOVER MORE",
                },
                ar: {
                    title: "",
                    description: "",
                    buttonText: "اكتشف المزيد",
                },
                buttonLink: "/products"
            },
            aboutUsSection: {
                imageUrl: "" // URL for the About Us hero image
            }
        };

        // Return content for the requested language
        return {
            heroSection: {
                title: homePageContent.heroSection[language]?.title || homePageContent.heroSection.en?.title || "",
                subtitle: homePageContent.heroSection[language]?.subtitle || homePageContent.heroSection.en?.subtitle || "",
                mediaType: homePageContent.heroSection.mediaType || "video",
                mediaUrl: homePageContent.heroSection.mediaUrl || "/vid.mp4",
                overlayOpacity: homePageContent.heroSection.overlayOpacity || 0.5
            },
            storySection: {
                title: homePageContent.storySection[language]?.title || homePageContent.storySection.en?.title || "",
                description: homePageContent.storySection[language]?.description || homePageContent.storySection.en?.description || "",
                buttonText: homePageContent.storySection[language]?.buttonText || homePageContent.storySection.en?.buttonText || "DISCOVER MORE",
                buttonLink: homePageContent.storySection.buttonLink || "/products"
            },
            aboutUsSection: {
                imageUrl: homePageContent.aboutUsSection?.imageUrl || ""
            }
        };
    } catch (error) {
        console.error("Error getting home page content:", error);
        throw error;
    }
};



/**
 * Get full multilingual home page content settings (for admin)
 * @param {string} storeId - The ID of the store
 * @returns {Promise<object>} Full home page content data with all languages
 */
/**
 * Get full multilingual home page content settings (for admin)
 * OPTIMIZED: Uses shared cache
 */
export const getFullHomePageContent = async (storeId) => {
    try {
        // ✅ Use the cache instead of getDoc
        const data = await getCachedStoreDoc(storeId);

        return data.homePageContent || {
            heroSection: {
                en: { title: "", subtitle: "" },
                ar: { title: "", subtitle: "" },
                mediaType: "video",
                mediaUrl: "/vid.mp4",
                overlayOpacity: 0.5
            },
            storySection: {
                en: { title: "", description: "", buttonText: "DISCOVER MORE" },
                ar: { title: "", description: "", buttonText: "اكتشف المزيد" },
                buttonLink: "/products"
            },
            aboutUsSection: {
                imageUrl: ""
            }
        };
    } catch (error) {
        console.error("Error getting full home page content:", error);
        throw error;
    }
};

